import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaSearch,
  FaHeart,
  FaUser,
  FaShoppingCart,
  FaChevronDown,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaSignInAlt,
  FaUserPlus,
} from "react-icons/fa";
import Flag from "react-world-flags";
import routes from "../routes";
import { ENABLE_CART_UI } from "../config/featureFlags";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, logout } = useAuth();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [isScrolled, setIsScrolled] = useState(false);
  const [language, setLanguage] = useState(i18n.language || "en");
  const [desktopLangDropdownOpen, setDesktopLangDropdownOpen] = useState(false);
  const [mobileLangDropdownOpen, setMobileLangDropdownOpen] = useState(false);
  const [mobileProductsDropdownOpen, setMobileProductsDropdownOpen] =
    useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const productMenuItems = [
    { label: t("navbar.productsMenu.allProducts"), path: routes.products },
    { label: t("navbar.productsMenu.wheatBag"), path: "/wheatbag" },
    { label: t("navbar.productsMenu.fridgeFreshness"), path: "/fridgeDeo" },
    { label: t("navbar.productsMenu.giftsPackages"), path: "/giftProducts" },
    {
      label: t("navbar.productsMenu.kitchenProducts"),
      path: "/kitchenProducts",
    },
    {
      label: t("navbar.productsMenu.japaneseProducts"),
      path: "/japaneseProducts",
    },
    {
      label: t("navbar.productsMenu.handmadeProducts"),
      path: "/handmadeProducts",
    },
    { label: t("navbar.productsMenu.wallArt"), path: "/wallarts" },
  ];

  const primaryNavRoutes = {
    home: routes.home,
    products: routes.products,
    aboutUs: routes.aboutUs,
    contactUs: routes.contactUs,
  };

  const languageMap = {
    en: "GB",
    tr: "TR",
    ar: "SA",
    ja: "JP",
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleLanguageChanged = (lang) => {
      setLanguage(lang);
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get("q") || "");
  }, [location.search]);

  const changeLanguage = (lang, isMobile = false) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    setDesktopLangDropdownOpen(false);
    setMobileLangDropdownOpen(false);
    if (isMobile) {
      setMenuOpen(false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchQuery.trim();

    if (!trimmed) {
      navigate(routes.products);
      setSearchOpen(false);
      return;
    }

    navigate(`${routes.products}?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
  };

  return (
    <nav
      dir="ltr"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-[#fffffff5] shadow-[0_2px_10px_0_rgba(152,72,57,0.8)] text-[#984839]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
        <Link
          to="/"
          onClick={() => window.scrollTo(0, 0)}
          className="flex items-center space-x-1 cursor-pointer"
        >
          <div className="relative h-14 w-[56px] shrink-0">
            <img
              src="/images/crossLogo2.png"
              alt="Logo"
              className={`absolute inset-0 h-14 w-auto transition-opacity duration-500 ${
                !isScrolled && isHomePage ? "opacity-100" : "opacity-0"
              }`}
            />
            <img
              src="/images/crossLogo1.png"
              alt="Logo"
              className={`absolute inset-0 h-14 w-auto transition-opacity duration-500 ${
                !isScrolled && isHomePage ? "opacity-0" : "opacity-100"
              }`}
            />
          </div>
          <span
            className={`text-lg font-light font-itim transition-all duration-500 ${
              isScrolled
                ? "text-[#984839] "
                : isHomePage
                  ? "text-white "
                  : "text-[#9d5a4ee6] "
            }`}
            style={{
              fontFamily: '"Dancing Script"',
              fontWeight: "600",
            }}
          >
            Cross Border
          </span>
        </Link>

        <ul
          className={`hidden md:flex font-light font-itim transition-all duration-500 ${
            isScrolled
              ? "text-[rgb(157_90_78_/_90%)] "
              : isHomePage
                ? "text-white "
                : "text-[#9d5a4ee6] "
          }`}
        >
          {["home", "products", "aboutUs", "contactUs"].map((key, index) => (
            <li
              key={index}
              className="group relative flex items-center space-x-1 mx-6"
            >
              <Link
                to={primaryNavRoutes[key]}
                className="transition-colors duration-450 group-hover:text-[#dd8d8d]"
              >
                {i18n.t(`navbar.${key}`)}
              </Link>
              {key === "products" && (
                <>
                  <FaChevronDown
                    className={`text-sm transition-transform duration-200 group-hover:rotate-180 group-hover:fill-[#dd8d8d] ${
                      isScrolled
                        ? "fill-[#8A5F5F]"
                        : isHomePage
                          ? "fill-white"
                          : "fill-[#9d5a4ee6]"
                    }`}
                  />
                  <div className="absolute left-1/2 top-full z-10 w-max min-w-[250px] -translate-x-1/2 pt-3 opacity-0 -translate-y-1 scale-[0.98] pointer-events-none transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100">
                    <ul className="overflow-hidden rounded-[14px] border border-white/12 bg-[rgba(34,24,30,0.78)] backdrop-blur-xl shadow-[0_18px_45px_-18px_rgba(20,12,16,0.55)] supports-[backdrop-filter]:bg-[rgba(34,24,30,0.68)]">
                      {productMenuItems.map((item) => (
                        <li
                          key={item.path}
                          className="border-b border-white/10 text-sm font-medium text-white/90 last:border-b-0"
                        >
                          <Link
                            to={item.path}
                            className="block px-4 py-2.5 transition-all duration-200 hover:bg-white/12 hover:pl-5 hover:text-white"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        <div
          className={` hidden md:flex flex items-center space-x-6 font-light font-itim transition-all duration-500 ${
            isScrolled
              ? "text-[#984839] "
              : isHomePage
                ? "text-white "
                : "text-[#9d5a4ee6] "
          }`}
        >
          <div
            className="relative pb-2 -mb-2"
            onMouseEnter={() => {
              setDesktopLangDropdownOpen(true);
              setUserDropdownOpen(false);
            }}
            onMouseLeave={() => setDesktopLangDropdownOpen(false)}
          >
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() =>
                setDesktopLangDropdownOpen(!desktopLangDropdownOpen)
              }
            >
              <Flag
                code={languageMap[language] || "GB"}
                style={{ width: "20px", height: "20px" }}
              />
              <span>{language.toUpperCase()}</span>
              <FaChevronDown
                className={`text-sm transition-transform duration-200 ${
                  desktopLangDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </div>
            {desktopLangDropdownOpen && (
              <div className="absolute right-0 top-full z-10">
                <ul className="w-max min-w-[130px] overflow-hidden rounded-[14px] border border-white/12 bg-[rgba(34,24,30,0.78)] backdrop-blur-xl shadow-[0_18px_45px_-18px_rgba(20,12,16,0.55)] supports-[backdrop-filter]:bg-[rgba(34,24,30,0.68)]">
                  {[
                    { code: "GB", lang: "en" },
                    { code: "TR", lang: "tr" },
                    { code: "SA", lang: "ar" },
                    { code: "JP", lang: "ja" },
                  ].map(({ code, lang }) => (
                    <li
                      key={lang}
                      onClick={() => changeLanguage(lang)}
                      className="px-4 py-2.5 cursor-pointer border-b border-white/10 text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:text-white last:border-b-0 flex items-center justify-center gap-2 text-center"
                    >
                      <Flag
                        code={code}
                        style={{ width: "20px", height: "20px" }}
                      />
                      <span>{lang.toUpperCase()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <span
            className="cursor-pointer"
            onClick={() => setSearchOpen((prev) => !prev)}
          >
            <FaSearch />
          </span>
          <span
            className="text-[#bf4f4f] hover:text-[#cf6666] transition-colors cursor-pointer"
            onClick={() => navigate("/profile?tab=wishlist")}
          >
            <FaHeart />
          </span>
          {ENABLE_CART_UI && (
            <span className="cursor-pointer" onClick={() => navigate("/cart")}>
              <FaShoppingCart />
            </span>
          )}

          <div
            className="relative pb-2 -mb-2"
            onMouseEnter={() => {
              setUserDropdownOpen(true);
              setDesktopLangDropdownOpen(false);
            }}
            onMouseLeave={() => setUserDropdownOpen(false)}
          >
            <span
              className="cursor-pointer"
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            >
              <FaUser />
            </span>
            {userDropdownOpen && (
              <div className="absolute right-0 top-full z-10 translate-x-1/2">
                <ul className="w-max min-w-[170px] overflow-hidden rounded-[14px] border border-white/12 bg-[rgba(34,24,30,0.78)] backdrop-blur-xl shadow-[0_18px_45px_-18px_rgba(20,12,16,0.55)] supports-[backdrop-filter]:bg-[rgba(34,24,30,0.68)]">
                  {isLoggedIn ? (
                    <>
                      <li
                        className="px-4 py-2.5 cursor-pointer border-b border-white/10 text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:pl-5 hover:text-white flex items-center space-x-2"
                        onClick={() => {
                          navigate(routes.profile);
                          setUserDropdownOpen(false);
                        }}
                      >
                        <FaUser /> <span>{t("navbar.auth.profile")}</span>
                      </li>
                      <li
                        className="px-4 py-2.5 cursor-pointer text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:pl-5 hover:text-white flex items-center space-x-2"
                        onClick={() => {
                          logout();
                          setUserDropdownOpen(false);
                        }}
                      >
                        <FaSignOutAlt /> <span>{t("navbar.auth.logout")}</span>
                      </li>
                    </>
                  ) : (
                    <>
                      <li
                        className="px-4 py-2.5 cursor-pointer border-b border-white/10 text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:pl-5 hover:text-white flex items-center space-x-2"
                        onClick={() => {
                          navigate(routes.login);
                          setUserDropdownOpen(false);
                        }}
                      >
                        <FaSignInAlt /> <span>{t("navbar.auth.login")}</span>
                      </li>
                      <li
                        className="px-4 py-2.5 cursor-pointer text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:pl-5 hover:text-white flex items-center space-x-2"
                        onClick={() => {
                          navigate(routes.signup);
                          setUserDropdownOpen(false);
                        }}
                      >
                        <FaUserPlus /> <span>{t("navbar.auth.signup")}</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="md:hidden ml-auto flex items-center gap-4">
          <div
            className={`flex items-center gap-3 font-light font-itim transition-color duration-500 ${
              !isScrolled && isHomePage ? "text-white " : "text-[#984839] "
            }`}
          >
            <span
              className="cursor-pointer"
              onClick={() => setSearchOpen((prev) => !prev)}
            >
              <FaSearch />
            </span>
            <span
              className="cursor-pointer text-[#681d1d] hover:text-[#cf6666] transition-colors"
              onClick={() => navigate("/profile?tab=wishlist")}
            >
              <FaHeart />
            </span>
          </div>

          <button
            className={`transition-colors duration-500 ${
              !isScrolled && isHomePage ? "text-white " : "text-[#8A5F5F] "
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <FaBars size={24} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-white/25 bg-[rgba(255,255,255,0.95)] px-4 py-3 shadow-[0_8px_20px_-14px_rgba(20,12,16,0.45)] backdrop-blur-md">
          <form
            onSubmit={handleSearchSubmit}
            className="mx-auto flex w-full max-w-7xl items-center gap-2"
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("navbar.searchPlaceholder", "Search products...")}
              className="w-full rounded-xl border border-[#d8b7c6] bg-white px-4 py-2.5 text-sm text-[#4f3d45] outline-none transition-all focus:border-[#AA7F96] focus:ring-2 focus:ring-[#AA7F96]/25"
            />
            <button
              type="submit"
              className="rounded-xl bg-[#83375B] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#6d2c4d]"
            >
              {t("navbar.searchButton", "Search")}
            </button>
          </form>
        </div>
      )}

      <div
        className={`fixed inset-y-0 right-0 h-[100dvh] max-h-[100dvh] w-2/3 bg-[rgb(228_219_219_/_91%)] shadow-lg transform transition-transform duration-300 md:hidden ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex shrink-0 justify-end px-5 pt-5 pb-3">
            <button
              className="text-[#8A5F5F]"
              onClick={() => {
                setMenuOpen(false);
                setMobileLangDropdownOpen(false);
                setMobileProductsDropdownOpen(false);
              }}
            >
              <FaTimes size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-6">
            <ul className="flex items-center text-[#8A5F5F] flex flex-col space-y-6 mt-2">
              {["home", "products", "aboutUs", "contactUs"].map(
                (key, index) => (
                  <li key={index} className="text-lg w-full text-center">
                    {key === "products" ? (
                      <button
                        type="button"
                        className="w-full flex items-center justify-center gap-2"
                        onClick={() =>
                          setMobileProductsDropdownOpen(
                            !mobileProductsDropdownOpen,
                          )
                        }
                      >
                        {i18n.t(`navbar.${key}`)}
                        <FaChevronDown
                          className={`text-xs transition-transform duration-200 ${
                            mobileProductsDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    ) : (
                      <Link
                        to={primaryNavRoutes[key]}
                        className="w-full flex items-center justify-center gap-2"
                        onClick={() => {
                          setMenuOpen(false);
                          setMobileLangDropdownOpen(false);
                          setMobileProductsDropdownOpen(false);
                        }}
                      >
                        {i18n.t(`navbar.${key}`)}
                      </Link>
                    )}

                    {key === "products" && mobileProductsDropdownOpen && (
                      <ul className="mt-3 space-y-1 rounded-xl border border-white/12 bg-[rgba(34,24,30,0.78)] p-3 text-sm text-white/90 shadow-[0_14px_32px_-20px_rgba(20,12,16,0.45)] backdrop-blur-xl supports-[backdrop-filter]:bg-[rgba(34,24,30,0.68)]">
                        {productMenuItems.map((item) => (
                          <li key={item.path}>
                            <Link
                              to={item.path}
                              className="w-full rounded-md px-2 py-1.5 text-left font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:pl-3 hover:text-white"
                              onClick={() => {
                                setMenuOpen(false);
                                setMobileLangDropdownOpen(false);
                                setMobileProductsDropdownOpen(false);
                              }}
                            >
                              {item.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ),
              )}
            </ul>

            <div className="flex flex-col space-y-4 mt-6 items-center pb-6">
              {isLoggedIn ? (
                <>
                  <button
                    className="text-[#8A5F5F] text-lg flex items-center space-x-2"
                    onClick={() => {
                      navigate(routes.profile);
                      setMenuOpen(false);
                      setMobileLangDropdownOpen(false);
                      setMobileProductsDropdownOpen(false);
                    }}
                  >
                    <FaUser /> <span>{t("navbar.auth.profile")}</span>
                  </button>
                  <button
                    className="text-[#8A5F5F] text-lg flex items-center space-x-2"
                    onClick={() => {
                      logout();
                      setMenuOpen(false);
                      setMobileLangDropdownOpen(false);
                      setMobileProductsDropdownOpen(false);
                    }}
                  >
                    <FaSignOutAlt /> <span>{t("navbar.auth.logout")}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="text-[#8A5F5F] text-lg flex items-center space-x-2"
                    onClick={() => {
                      navigate(routes.login);
                      setMenuOpen(false);
                      setMobileLangDropdownOpen(false);
                      setMobileProductsDropdownOpen(false);
                    }}
                  >
                    <FaSignInAlt /> <span>{t("navbar.auth.login")}</span>
                  </button>
                  <button
                    className="text-[#8A5F5F] text-lg flex items-center space-x-2"
                    onClick={() => {
                      navigate(routes.signup);
                      setMenuOpen(false);
                      setMobileLangDropdownOpen(false);
                      setMobileProductsDropdownOpen(false);
                    }}
                  >
                    <FaUserPlus /> <span>{t("navbar.auth.signup")}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-white/40 bg-[rgba(255,255,255,0.35)] px-5 py-4 text-[#8A5F5F]">
            <div className="mx-auto w-fit">
              <button
                type="button"
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() =>
                  setMobileLangDropdownOpen(!mobileLangDropdownOpen)
                }
              >
                <Flag
                  code={languageMap[language] || "GB"}
                  style={{ width: "20px", height: "20px" }}
                />
                <span>{language.toUpperCase()}</span>
                <FaChevronDown
                  className={`text-sm transition-transform ${
                    mobileLangDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {mobileLangDropdownOpen && (
                <ul className="mt-2 w-max min-w-[130px] mx-auto overflow-hidden rounded-[14px] border border-white/12 bg-[rgba(34,24,30,0.78)] backdrop-blur-xl shadow-[0_18px_45px_-18px_rgba(20,12,16,0.55)] supports-[backdrop-filter]:bg-[rgba(34,24,30,0.68)]">
                  {["en", "tr", "ar", "ja"].map((lang) => (
                    <li
                      key={lang}
                      onClick={() => changeLanguage(lang, true)}
                      className="px-4 py-2.5 cursor-pointer border-b border-white/10 text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/12 hover:text-white last:border-b-0 flex items-center justify-center gap-2 text-center"
                    >
                      <Flag
                        code={languageMap[lang]}
                        style={{ width: "20px", height: "20px" }}
                      />
                      <span>{lang.toUpperCase()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
